import { SisSettingState } from '../../redux/ui/sisSettings';
import {
  resolveGraphSlotChartHeight,
  resolveGraphSlotContainerHeight,
} from '../graphSlotLayout';
import { KiTagListOptions, MainFieldType, OrderType } from '../schemas';
import { SisDataType } from '../settingsShuGroups';
import { sisDateRanges, sisDateUnits } from './consistent';

/**
 * [SIS機種レポート] 期間の型
 */
export type SisDateRange = (typeof sisDateRanges)[number]['name'];

/**
 * [SIS機種レポート] カレンダー内で使用される期間単位の型
 */
export type SisDateUnit = (typeof sisDateUnits)[number]['name'];

/**
 * [SIS機種レポート] settingOptionsのレスポンス
 */
export type SettingsOptionsSis = {
  searchCondition: {
    sisDataTypes: SisDataType[];
    latestBaseDateForDailySisData: string;
    latestBaseDateForWeeklySisData: string;
    kiTagList: KiTagListOptions[];
  };
  fields: {
    ki: {
      code: string;
      name: string;
      isDefault: boolean;
      isEnabled: boolean;
      isSelectableForGraph: boolean;
      viewOrder: number;
      unsupportedMainFields: MainFieldType[];
      supportsSis: boolean;
      genres: string[];
      description: string;
      isNew: boolean;
    }[];
  };
};

/**
 * [SIS機種レポート] テーブルデータのレスポンス
 */
export type DataSis = {
  setting: Partial<{
    sisDataType: SisDataType | null;
    fields: string[];
    sisStartDate: string;
    sisDateRangeType: 'daily' | 'weekly';
    sort: string;
    order: OrderType;
  }>;
  data: {
    columns: {
      name: string;
      isSisField: boolean | null;
      code: string | null;
      type: 'number' | 'numberWithPrefix' | 'string' | 'numberOrString' | null;
      backgroundColor: string | null;
      color: string | null;
      description: string | null;
    }[];
    rows: {
      queryParameter: {
        kiList: string[] | null;
      };
      data: {
        value: string;
        color: string;
        backgroundColor: string | null;
      }[];
    }[];
  };
};

/**
 * [SIS機種レポート] グラフデータのレスポンス
 */
export const DATA_SIS_GRAPH_MAX_FIELDS = 5;

export const DATA_SIS_GRAPH_SYNC_ID = 'sisGraph';

export const resolveDataSisGraphChartHeight = (graphCount: number): number =>
  resolveGraphSlotChartHeight(graphCount);

/** タイトル・機種選択・枠線など、グラフ本体以外の高さ（px） */
const DATA_SIS_GRAPH_CONTAINER_CHROME_HEIGHT = 48;
const DATA_SIS_GRAPH_CONTAINER_TITLE_HEIGHT = 48;
const DATA_SIS_GRAPH_CONTAINER_CONTROLS_HEIGHT = 104;
const DATA_SIS_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT = 56;
const DATA_SIS_GRAPH_CONTAINER_SLOT_GAP = 16;
const DATA_SIS_GRAPH_CONTAINER_FOOTER_HEIGHT = 52;

/**
 * 複数スロット表示時に ResizableWrapper が収めるべき高さ（px）を返す。
 * 機種比較の推移グラフと同様、グラフ本数に応じて領域を広げる。
 */
export const resolveDataSisGraphContainerHeight = (slotCount: number): number =>
  resolveGraphSlotContainerHeight(slotCount, {
    chromeHeight: DATA_SIS_GRAPH_CONTAINER_CHROME_HEIGHT,
    titleHeight: DATA_SIS_GRAPH_CONTAINER_TITLE_HEIGHT,
    controlsHeight: DATA_SIS_GRAPH_CONTAINER_CONTROLS_HEIGHT,
    slotControlsHeight: DATA_SIS_GRAPH_CONTAINER_SLOT_CONTROLS_HEIGHT,
    slotGap: DATA_SIS_GRAPH_CONTAINER_SLOT_GAP,
    footerHeight: DATA_SIS_GRAPH_CONTAINER_FOOTER_HEIGHT,
    resolveChartHeight: resolveDataSisGraphChartHeight,
  });

export type GraphSisData = {
  columns: {
    name: string;
    type: 'number' | 'string';
    digitsAfterDecimalPoint: number | null;
    code: string | null;
  }[];
  rows: (string | number | null)[][];
};

export type GraphSisParams = Partial<{
  field: string;
  fields: string[];
  kiList: string[];
  // バックエンドのAPI定義に'monthly' | 'any'が存在するが、返ってくる事はないので削除しています
  dateType: 'daily' | 'weekly';
  sisDataType: SisDataType;
}>;

export type GraphSisSetting = Omit<GraphSisParams, 'field' | 'fields'>;

export type GraphSisFieldItem = {
  field: string;
  data: GraphSisData;
};

export type GraphSisApiResponse = {
  setting: GraphSisParams;
  data: GraphSisData;
};

export type GraphSis = {
  setting: GraphSisSetting;
  graphList: GraphSisFieldItem[];
};

export const toGraphSisSetting = (params: GraphSisParams): GraphSisSetting => {
  const { field: _field, fields: _fields, ...setting } = params;
  return setting;
};

/**
 * SIS機種レポートのお気に入りの型
 */
export type SisFavorite = {
  /**
   * 現在の検索条件
   */
  searchParams: DataSis['setting'] | undefined;
  /**
   * サブメニュー内数値フィルタ
   */
  subMenuNumberFilter:
    | {
        [field: string]: {
          minimumNumber: number | undefined;
          maximumNumber: number | undefined;
        };
      }
    | undefined;
  /**
   * 機種名フィルタ
   */
  nameFilter: string | undefined;
  /**
   * テーブルの非表示項目一覧
   */
  fieldsFilter: string[];
  /**
   * 機種絞り込み
   */
  kiListFilter: string[];
  /**
   * 表示項目の列順
   */
  columnsOrder: string[];
  /**
   * SisDateSelectで選択している期間のタイプ
   */
  selectedDateRange: SisDateRange;
  /**
   * タグ絞込で選択されているタグリスト
   */
  sisKiTagList?: KiTagListOptions[];
  /**
   * 機種行への機種タグ表示機能フラグ
   */
  sisShowKiTagLabel?: boolean;
  /**
   * テーブルのチェックボックスで選択されている機種
   */
  checkedKiList: {
    dataType: string;
    ki: string;
  }[];
  /**
   * 推移グラフ
   */
  dataSisGraph:
    | {
        /**
         * グラフの検索条件
         */
        searchParams: GraphSisParams;
        /**
         * 推移グラフの数値表示
         */
        showGraphNumberLabel: boolean;
        /**
         * 選択中の機種（機種選択セレクタで絞り込んだ機種）
         */
        selectKi: string[];
      }
    | undefined;
  /**
   * 特定項目ヘッダーにおけるサブメニュー上にあるマルチフィルターの選択状態を保持（key: カラム名、value: 選択中のフィルター項目）
   */
  tableHeaderSubMenuMultiFilter?: SisSettingState['tableHeaderSubMenuMultiFilter'];
};
